import get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { format } from 'date-fns';
import {
  SRL_COMPONENT_TYPES,
  WIDGET_TYPE_TIME_SCORE_CHART,
  LOGIN_SLICE_NAMES,
} from './constants';

const getComponentByType = (insightsData = [], componentType) =>
  insightsData.find((component) => component.componentType === componentType);
export const getComponentsByType = (insightsData = [], componentType) =>
  insightsData.filter((component) => component.componentType === componentType);

export const selectAnswerChangeData = (insightsData) => {
  const percentageObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.ANSWER_CHANGE
  );
  return get(percentageObj, 'componentData', {});
};

export const selectAverageData = (insightsData) => {
  const averageObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.AVERAGE
  );
  return get(averageObj, 'componentData[0]', {});
};

export const selectMedianData = (insightsData) => {
  const averageObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.MEDIAN
  );
  return get(averageObj, 'componentData[0]', {});
};

export const selectChartData = (insightsData) => {
  const ChartDataObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.COLUMN_CHART
  );
  return get(ChartDataObj, 'componentData', []);
};

export const selectTextListData = (insightsData) => {
  const studentResponsesObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.TEXT_LIST
  );
  const prompt = get(studentResponsesObj, 'componentDescription', '');
  const studentResponses = get(studentResponsesObj, 'componentData', []);
  return { prompt, studentResponses };
};

export const selectDifferenceData = (insightsData) => {
  const differenceObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.DIFFERENCE
  );
  return get(differenceObj, 'componentData[0]', {});
};

export const selectMedianDifferenceData = (insightsData) => {
  const differenceObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.MEDIAN_DIFFERENCE
  );
  return get(differenceObj, 'componentData[0]', {});
};

export const selectPercentageData = (insightsData) => {
  const percentageObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.PERCENTAGE
  );
  return get(percentageObj, 'componentData', {});
};

export const selectBarChartData = (insightsData) => {
  const ChartDataObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.BAR_CHART
  );
  return get(ChartDataObj, 'componentData', []);
};

export const selectStackedBardChartData = (insightsData) => {
  const stackedBarObj = getComponentByType(
    insightsData,
    SRL_COMPONENT_TYPES.STACKED_BAR_CHART
  );
  return get(stackedBarObj, 'componentData', {});
};

export const selectAssignmentDetailsData = (assignmentData) => {
  const studentObj = get(assignmentData, 'studentData', []);
  return studentObj;
};

export const selectAssignmentScoreAverage = (assignmentData) => {
  const scoreObj = get(assignmentData, 'averageScore', []);
  return scoreObj;
};

export const selectAssignmentTimeAverage = (assignmentData) => {
  const timeObj = get(assignmentData, 'averageTime', []);
  return timeObj;
};

export const getComponentByDescription = (data = [], description = '') => {
  const descriptionComponent = data.find((component) => {
    const { componentDescription = '' } = component;
    // remove - ${number} to get text before the '-' from description
    const componentDescriptionFormatted = componentDescription
      .split('-')[0]
      .trim();
    return (
      componentDescriptionFormatted.toLowerCase() === description.toLowerCase()
    );
  });
  const item = descriptionComponent || {};
  const { componentData = [] } = item;
  return componentData;
};

export const selectChartDataByDescription = (
  insightsData,
  componentType = '',
  description = ''
) => {
  const chartComponents = getComponentsByType(insightsData, componentType);
  const chartList = getComponentByDescription(chartComponents, description);
  return chartList;
};

export const getFormattedDate = (date) => {
  if (date) {
    return format(new Date(date), "EEE, MMM d 'at' h:mmaaa");
  }
  return 'Unopened';
};

export const getHoursAndMinutes = (totalMinutes) => {
  let hours = 0;
  let minutes = totalMinutes;
  if (minutes > 60) {
    hours = Math.floor(totalMinutes / 60);
    minutes = totalMinutes % 60;
  }
  return { hours, minutes };
};

export const getFormattedTimeSpent = (totalMinutes) => {
  if (!totalMinutes || !Number.isFinite(totalMinutes)) {
    return '--';
  }

  const { hours, minutes } = getHoursAndMinutes(totalMinutes);

  let value;
  if (hours) {
    value = `${hours}h${Math.round(minutes)}m`;
  } else {
    value = `${Math.round(minutes)}m`;
  }
  return value;
};

export const getFormattedAverage = (
  value,
  decimals = 0,
  isPercentage = false
) => {
  if (!value || !Number.isFinite(value)) {
    return '--';
  }

  let unit = '';
  let formattedValue = value;
  if (isPercentage) {
    formattedValue *= 100;
    unit = '%';
  }
  return `${Number.parseFloat(formattedValue).toFixed(decimals)}${unit}`;
};

export const sortStudentsByName = (students, direction = 'asc') =>
  students.sort((a, b) => {
    const aLastName = a.lastName || a.last_name || a[0].last_name;
    const bLastName = b.lastName || b.last_name || b[0].last_name;
    const aFirstName = a.firstName || a.first_name || a[0].first_name;
    const bFirstName = b.firstName || b.first_name || b[0].first_name;
    const fullNameA = aLastName.concat(', ').concat(aFirstName);
    const fullNameB = bLastName.concat(', ').concat(bFirstName);
    if (direction === 'asc') {
      return fullNameA.localeCompare(fullNameB, 'en', { sensitivity: 'base' });
    }
    return fullNameB.localeCompare(fullNameA, 'en', { sensitivity: 'base' });
  });

export const normalizeString = (str) =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export const getBrand = () => {
  return window.brand || 'he';
};

export const filteredView = (assignmentViewsRelation, viewId) => {
  const filtered = [];
  if (!_isEmpty(assignmentViewsRelation)) {
    for (let i = 0, len = assignmentViewsRelation.length; i < len; i++) {
      const relation = assignmentViewsRelation[i];
      if (relation.assignment_views_id === viewId) {
        filtered.push(relation);
      }
    }
  }
  return filtered;
};

// Accessibility utility functions for charts
const formatValue = (value, decimalPlaces = 1) => {
  if (value) return +value.toFixed(decimalPlaces);
  return 0;
};

const sumValues = (items) =>
  Array.isArray(items) ? items.reduce((sum, d) => sum + (d.value || 0), 0) : 0;

export const generateChartDescription = (data, type) => {
  const activityCount = data.insightsData?.length || 0;
  const avgTime = formatValue(data.averageTime);
  const avgScore = formatValue(data.averageScore);
  const avgAttempts = formatValue(data.averageAttempts);

  const chartType =
    type === WIDGET_TYPE_TIME_SCORE_CHART
      ? 'time versus score'
      : 'time versus attempts';
  const yAxisDesc =
    type === WIDGET_TYPE_TIME_SCORE_CHART
      ? `activity scores ranging from 0 to 100 percent, with an average of ${avgScore} percent`
      : `total attempts, with an average of ${avgAttempts} attempts`;

  return (
    `Scatter chart showing ${chartType} for ${activityCount} activities. ` +
    `The horizontal axis shows time in minutes with an average of ${avgTime} minutes. ` +
    `The vertical axis shows ${yAxisDesc}. ` +
    `Reference lines indicate the average values for both axes.`
  );
};

export const getChartAltText = (data, type) => {
  const chartType =
    type === WIDGET_TYPE_TIME_SCORE_CHART
      ? 'Time vs Score'
      : 'Time vs Attempts';
  const activityCount = data.insightsData?.length || 0;
  return `${chartType} scatter chart displaying ${activityCount} activities with average reference lines`;
};

export const getLoginPieA11yLabel = (data) => {
  const slices = Array.isArray(data?.insightsData) ? data.insightsData : [];
  const loggedIn =
    slices.find((d) => d.name === LOGIN_SLICE_NAMES.LOGGED_IN)?.value || 0;
  const notLoggedIn =
    slices.find((d) => d.name === LOGIN_SLICE_NAMES.NOT_LOGGED_IN)?.value || 0;
  const total = loggedIn + notLoggedIn;
  return (
    `Student login pie chart: ${loggedIn} of ${total} students have logged in, ` +
    `${notLoggedIn} have not logged in`
  );
};

export const getLoginBucketsPieA11yLabel = (loginData) => {
  const total = sumValues(loginData);
  if (!Array.isArray(loginData) || loginData.length === 0) {
    return `Student login distribution pie chart: no login data available`;
  }
  const segments = loginData
    .map((d) => `${d.value} students logged in ${d.name} times`)
    .join(', ');
  return `Student login distribution pie chart: ${total} total students. ${segments}`;
};

export const preventDescendantFocus = (e) => {
  if (e.target !== e.currentTarget) e.preventDefault();
};
